// src/pages/UserManagement.js

import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiX, FiInfo, FiLock, FiRefreshCw, FiSearch, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import ListView from '../components/ListView';
import CheckboxGroup from '../components/CheckboxGroup';
import CustomFileUpload from '../components/CustomFileUpload';
import { useApi } from '../hooks/useApi';
import { useModal } from '../context/ModalContext';
import { AuthContext } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';

const ALL_ROLES = ['User', 'Admin', 'Giga Admin', 'Client'];
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// =================================================================================
// USER FORM MODAL
// =================================================================================
const UserFormModal = ({ isOpen, onClose, onSave, user, isEditMode, departments, communities }) => {
    const initialState = { name: '', email: '', password: '', roles: ['User'], departments: [], linkedCommunityId: '' };
    const [userData, setUserData] = useState(initialState);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    
    const fileUploadRef = useRef(null);
    const { showAlert } = useModal();
    
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && user) {
                setUserData({ ...initialState, ...user, password: '' });
                setPreviewUrl(user.profilePictureUrl || '');
                setShowPasswordReset(false); 
            } else {
                setUserData(initialState);
                setPreviewUrl('');
                setShowPasswordReset(false);
            }
            setSelectedFile(null);
            if (fileUploadRef.current) fileUploadRef.current.clear();
        }
    }, [isOpen, user, isEditMode]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                e.preventDefault();
                onClose();
            }
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (e) => { setUserData(prev => ({ ...prev, [e.target.name]: e.target.value })); };
    const handleRolesChange = (selectedRoles) => { setUserData(prev => ({...prev, roles: selectedRoles})); };
    const handleDepartmentsChange = (selectedDepts) => { setUserData(prev => ({...prev, departments: selectedDepts})); };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (userData.roles.includes('Client') && !userData.linkedCommunityId) {
            showAlert("A Client user must be assigned to a Community.", 'Validation Error');
            return;
        }
        onSave(userData, selectedFile);
    };

    const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : '';

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>{isEditMode ? 'Edit User' : 'Add New User'}</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX/></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                    <div className="modal-body" style={{ overflowY: 'auto' }}>
                        <div className="profile-section">
                            {previewUrl ? (
                                <img 
                                    src={previewUrl} 
                                    alt="Profile Preview" 
                                    className="profile-picture-preview" 
                                    onClick={() => fileUploadRef.current && fileUploadRef.current.click()}
                                    title="Click to upload"
                                />
                            ) : (
                                <div 
                                    className="profile-picture-preview"
                                    onClick={() => fileUploadRef.current && fileUploadRef.current.click()}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--gray-200)', color: 'var(--gray-600)', fontSize: '2.5rem', fontWeight: 'bold', textTransform: 'uppercase', cursor: 'pointer' }}
                                >
                                    {userInitial}
                                </div>
                            )}
                            <CustomFileUpload
                                ref={fileUploadRef}
                                label={isEditMode ? "Change Picture" : "Upload Picture"}
                                onChange={handleFileChange}
                                accept="image/*"
                                variant="secondary"
                                showPreviewText={false}
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Full Name</label>
                                <input type="text" name="name" value={userData.name} onChange={handleChange} required className="form-input" />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Email</label>
                                <input type="email" name="email" value={userData.email} onChange={handleChange} required className="form-input" />
                            </div>
                        </div>
                        {!isEditMode ? (
                            <div className="form-group">
                                <label>Password</label>
                                <input type="password" name="password" value={userData.password} onChange={handleChange} required className="form-input" />
                            </div>
                        ) : (
                            <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--gray-200)', paddingTop: '1.5rem' }}>
                                <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FiLock size={16} /> Security & Access
                                </h4>
                                {!showPasswordReset ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: 'var(--gray-5)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '0.9rem', fontWeight: '500', color: 'var(--gray-700)' }}>Password</span>
                                            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>••••••••••••••••</span>
                                        </div>
                                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPasswordReset(true)}>
                                            <FiRefreshCw className="btn-icon" /> Reset
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ backgroundColor: 'var(--orange-100)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--orange-200)' }}>
                                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ color: 'var(--orange-700)', fontWeight: '600' }}>Set New Password</label>
                                            <input type="text" name="password" value={userData.password} onChange={handleChange} className="form-input" autoFocus />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowPasswordReset(false); setUserData(prev => ({ ...prev, password: '' })); }}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <hr className="section-divider" />
                        <CheckboxGroup title="Assign Roles" options={ALL_ROLES} selected={userData.roles} onChange={handleRolesChange} />
                        {userData.roles.includes('Client') && (
                            <div className="client-assignment-box">
                                <label>Assign Client Community</label>
                                <select name="linkedCommunityId" value={userData.linkedCommunityId || ''} onChange={handleChange} className="form-select" required>
                                    <option value="" disabled>-- Select Community --</option>
                                    {communities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}
                        {!userData.roles.includes('Client') && (
                            <CheckboxGroup title="Assign Departments" options={departments.map(d => d.name)} selected={userData.departments} onChange={handleDepartmentsChange} />
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary">{isEditMode ? 'Save Changes' : 'Create User'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// =================================================================================
// MOBILE USER CARD COMPONENT
// =================================================================================
const MobileUserCard = ({ user, onEdit, onDelete, canManage, index = 0 }) => {
    return (
        <div 
            className="mobile-technician-card"
            style={{ 
                animation: 'slideFadeInWithGlow 0.4s ease-out backwards',
                animationDelay: `${index * 0.05}s`
            }}
        >
            <div className="mobile-technician-card-header">
                <div className="user-cell" style={{ gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    <div className="list-view-avatar" style={{ width: '40px', height: '40px' }}>
                        {user.profilePictureUrl ? (
                            <img src={user.profilePictureUrl} alt={user.name} className="list-view-avatar-img" />
                        ) : (
                            <span className="list-view-avatar-initials">{user.name ? user.name.charAt(0).toUpperCase() : '?'}</span>
                        )}
                    </div>
                    <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        <span className="mobile-technician-name" style={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user.name}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user.email}</span>
                    </div>
                </div>
                {canManage && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn-icon-only" onClick={() => onEdit(user)}><FiEdit2 size={20} /></button>
                        <button className="btn-icon-only btn-icon-danger" onClick={() => onDelete(user._id)}><FiTrash2 size={20} /></button>
                    </div>
                )}
            </div>
            <div className="mobile-technician-card-body" style={{ marginTop: '0.5rem', borderTop: '1px solid var(--gray-100)', paddingTop: '0.5rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {user.roles.map(role => <span key={role} className="tag type-tickets" style={{ fontSize: '0.75rem' }}>{role}</span>)}
                    {user.departments.map(dept => <span key={dept} className="tag" style={{ fontSize: '0.75rem' }}>{dept}</span>)}
                </div>
            </div>
        </div>
    );
};

// =================================================================================
// MAIN COMPONENT
// =================================================================================
const UserManagement = ({ departments, users, setUsers }) => { 
    const navigate = useNavigate();
    const location = useLocation();
    const callApi = useApi();
    const isMobile = useIsMobile();
    const { showAlert, showConfirm } = useModal();
    const { user: currentUser } = useContext(AuthContext); 
    
    const [communities, setCommunities] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [mobileSearchTerm, setMobileSearchTerm] = useState('');

    const canManageUsers = useMemo(() => 
        currentUser?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
        [currentUser]
    );

    useEffect(() => {
        const fetchCommunities = async () => {
            const commsRes = await callApi('communities');
            if (commsRes.success) setCommunities(commsRes.data);
        };
        fetchCommunities();
    }, [callApi]);

    useEffect(() => {
        if (location.state?.action === 'newUser' && canManageUsers) {
            handleOpenModal();
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, canManageUsers, navigate]);

    const handleSelectOne = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (isChecked, idsOnPage) => {
        if (isChecked) {
            setSelectedIds(prev => Array.from(new Set([...prev, ...idsOnPage])));
        } else {
            setSelectedIds(prev => prev.filter(id => !idsOnPage.includes(id)));
        }
    };

    const handleSaveUser = async (userData, file) => {
        const isEditMode = !!editingUser;
        const method = isEditMode ? 'PUT' : 'POST';
        const formData = new FormData();
        formData.append('name', userData.name);
        formData.append('email', userData.email);
        if (userData.password) formData.append('password', userData.password);
        if (userData.linkedCommunityId) formData.append('linkedCommunityId', userData.linkedCommunityId);
        userData.roles.forEach(role => formData.append('roles', role));
        userData.departments.forEach(dept => formData.append('departments', dept));
        if (file) formData.append('profilePicture', file);

        try {
            const response = await fetch(`${API_BASE_URL}/users${isEditMode ? `/${editingUser._id}` : ''}`, {
                method, headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: formData
            });
            const resultData = await response.json();
            if (!response.ok) throw new Error(resultData.message || 'Failed to save user');
            if (Array.isArray(resultData)) setUsers(resultData);
            setIsModalOpen(false);
            setEditingUser(null);
        } catch (error) { showAlert(error.message, 'API Error'); }
    };
    
    // Wrapper for single delete (Mobile)
    const handleDeleteSingleUser = (id) => {
        showConfirm(
            "Are you sure you want to delete this user? This cannot be undone.",
            () => handleDeleteUsers([id]),
            "Delete User",
            true
        );
    };

    const handleDeleteUsers = async (ids) => {
        const result = await callApi('users/delete-multiple', 'POST', { ids });
        if (result.success) {
            if (Array.isArray(result.data)) {
                 setUsers(result.data);
                 setSelectedIds([]);
            }
        } else {
            showAlert(`Error deleting users: ${result.error}`, 'Deletion Failed');
        }
    };

    const handleOpenModal = (user = null) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const columns = [
        { 
            header: 'Name', accessor: 'name', render: (user) => (
                <div className="user-cell">
                    <div className="list-view-avatar">
                        {user.profilePictureUrl ? <img src={user.profilePictureUrl} alt={user.name} className="list-view-avatar-img" /> : <span className="list-view-avatar-initials">{user.name?.charAt(0)}</span>}
                    </div>
                    <span>{user.name}</span>
                </div>
            )
        },
        { header: 'Email', accessor: 'email' },
        { header: 'Departments', accessor: 'departments', render: (user) => user.departments.join(', ') },
        { header: 'Roles', render: (user) => user.roles.join(', ') },
        { header: 'Date Added', render: (user) => new Date(user.createdAt).toLocaleDateString() }
    ];

    const filteredMobileUsers = useMemo(() => {
        const lower = mobileSearchTerm.toLowerCase();
        return users.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
    }, [users, mobileSearchTerm]);

    // =========================================================================
    // STRICT JS TOGGLE: PREVENTS BOTH VIEWS FROM EXISTING IN DOM
    // =========================================================================
    
    if (isMobile) {
        return (
            <>
                <UserFormModal 
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveUser}
                    user={editingUser}
                    isEditMode={!!editingUser}
                    departments={departments}
                    communities={communities}
                />
                <div className="page-container mobile-user-management-page">
                    <div className="page-header">
                        <h2 className="page-title">User Management</h2>
                    </div>

                    <div className="mobile-filter-bar">
                        <div className="search-input-wrapper">
                            <FiSearch className="search-icon" />
                            <input 
                                type="search" 
                                className="search-input" 
                                placeholder="Search users..." 
                                value={mobileSearchTerm} 
                                onChange={(e) => setMobileSearchTerm(e.target.value)} 
                            />
                        </div>
                    </div>

                    <div className="mobile-user-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0 0.75rem 0.75rem' }}>
                        {filteredMobileUsers.length > 0 ? (
                            filteredMobileUsers.map((user, index) => (
                                <MobileUserCard 
                                    key={user._id} 
                                    user={user} 
                                    onEdit={handleOpenModal} 
                                    onDelete={handleDeleteSingleUser} 
                                    canManage={canManageUsers} 
                                    index={index}
                                />
                            ))
                        ) : (
                            <div className="no-tasks-message">
                                <p>No users found.</p>
                            </div>
                        )}
                    </div>

                    {canManageUsers && (
                        <button className="fab-add-ticket" onClick={() => handleOpenModal()} title="Add New User">
                            <FiPlus size={24} />
                        </button>
                    )}
                </div>
            </>
        );
    }

    // --- DESKTOP RENDER ---
    return (
        <>
            <UserFormModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveUser}
                user={editingUser}
                isEditMode={!!editingUser}
                departments={departments}
                communities={communities}
            />
            
            <div className="page-container">
                <div className="page-header">
                    <div className="header-left-details">
                        <button onClick={() => navigate('/settings')} className="back-btn" title="Back to Settings">
                            <FiArrowLeft />
                        </button>
                        <div>
                            <h2 className="page-title">User Management</h2>
                            <span className="page-subtitle">Add, view, and manage application users.</span>
                        </div>
                    </div>
                    {canManageUsers && (
                        <div className="page-actions">
                            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                                <FiPlus size={16} className="btn-icon" /> Add User
                            </button>
                        </div>
                    )}
                </div>

                <ListView
                    items={users} 
                    columns={columns}
                    onNewItem={() => handleOpenModal()}
                    onDeleteItems={handleDeleteUsers}
                    onItemClick={(id) => handleOpenModal(users.find(u => u._id === id))}
                    searchKeys={['name', 'email']}
                    searchPlaceholder="Search by name or email..."
                    newItemButtonText="Add User"
                    itemNoun="user"
                    enableSelection={canManageUsers}
                    selectedIds={selectedIds}
                    onSelectOne={handleSelectOne}
                    onSelectAll={handleSelectAll}
                />
            </div>
        </>
    );
};

export default UserManagement;